// Accessibility mode — user-facing reading aids on top of the normal design
// (eesti.ee-style): text size, high contrast, reduced motion. Same mechanism
// as the theme (lib/theme.tsx): settings persist in localStorage and are
// stamped onto <html data-a11y-*="…">, which the CSS in index.css keys off.
// index.html stamps the attributes before first paint so nothing flashes.
// Device-level only — portal-api has no profile field for this.

import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type TextSize = 'normal' | 'large' | 'xlarge';

export type A11ySettings = {
  textSize: TextSize;
  highContrast: boolean;
  reduceMotion: boolean;
};

export const A11Y_STORAGE_KEY = 'a11ySettings';

const DEFAULTS: A11ySettings = {
  textSize: 'normal',
  highContrast: false,
  reduceMotion: false,
};

type A11yContextValue = {
  settings: A11ySettings;
  /** True when any aid is on — drives the topbar button's active state. */
  active: boolean;
  update: (patch: Partial<A11ySettings>) => void;
};

const A11yContext = createContext<A11yContextValue>({
  settings: DEFAULTS,
  active: false,
  update: () => {},
});

function readStoredSettings(): A11ySettings {
  try {
    const raw = window.localStorage.getItem(A11Y_STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const v = JSON.parse(raw) as Partial<A11ySettings>;
    return {
      textSize: v.textSize === 'large' || v.textSize === 'xlarge' ? v.textSize : 'normal',
      highContrast: v.highContrast === true,
      reduceMotion: v.reduceMotion === true,
    };
  } catch {
    return DEFAULTS;
  }
}

// Mirror of the pre-paint stamp in index.html — keep the two in sync.
function stamp(s: A11ySettings): void {
  const el = document.documentElement;
  if (s.textSize !== 'normal') el.dataset.a11yText = s.textSize;
  else delete el.dataset.a11yText;
  if (s.highContrast) el.dataset.a11yContrast = 'high';
  else delete el.dataset.a11yContrast;
  if (s.reduceMotion) el.dataset.a11yMotion = 'reduce';
  else delete el.dataset.a11yMotion;
}

// For JS-driven motion, which the CSS overrides can't reach. Honors both the
// explicit toggle and the OS preference.
export function prefersReducedMotion(): boolean {
  if (document.documentElement.dataset.a11yMotion === 'reduce') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// For scrollIntoView: CSS can't override the behavior option, so callers ask here.
export function motionSafeBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? 'auto' : 'smooth';
}

export function A11yProvider({ children }: { children: ReactNode }): ReactElement {
  const [settings, setSettings] = useState<A11ySettings>(readStoredSettings);

  useEffect(() => {
    stamp(settings);
    try {
      window.localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      /* best effort */
    }
  }, [settings]);

  const update = useCallback((patch: Partial<A11ySettings>) => {
    setSettings((s) => ({ ...s, ...patch }));
  }, []);

  const value = useMemo<A11yContextValue>(
    () => ({
      settings,
      active:
        settings.textSize !== 'normal' || settings.highContrast || settings.reduceMotion,
      update,
    }),
    [settings, update],
  );

  return <A11yContext.Provider value={value}>{children}</A11yContext.Provider>;
}

export function useA11y(): A11yContextValue {
  return useContext(A11yContext);
}
